import React, { useEffect, useState, useRef } from "react";
import "./AddMissionForm.css";
import PrimaryButton from "../../../Atoms/Buttons/Primary/PrimaryButton";
import useSave from "../../../../Hooks/useSave";
import useEdit from "../../../../Hooks/useEdit";
import { Formik } from "formik";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextArea from "../../../Atoms/Inputs/TextArea/TextArea";
import * as Yup from "yup";
import TextFieldInput from "../../../Atoms/Inputs/TextField/TextFieldInput";
import { DateTimePicker } from "@mui/x-date-pickers";
import {
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  IconButton,
  Radio,
  Card,
  CardContent,
  Typography,
  Grid,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import ImageIcon from "@mui/icons-material/Image";
import { BaseURL, missionsURL } from "../../../../Hooks/URL";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ErrorIcon from "@mui/icons-material/Error";
import dayjs from "dayjs";
import useUpdate from "../../../../Hooks/useUpdate";

const sessionSchema = Yup.object().shape({
  name: Yup.string().required("Mission Name required !!"),
  dateTime: Yup.date()
    .required("Date/Time required!")
    .typeError("Date/Time required!"),
  submissionStartDateTime: Yup.date()
    .required("Start Date/Time required!")
    .typeError("Start Date/Time required!"),
  submissionEndDateTime: Yup.date()
    .required("End Date/Time required!")
    .typeError("End Date/Time required!")
    .min(
      Yup.ref("submissionStartDateTime"),
      "End Date/Time must be after Start Date/Time!"
    ),
  order: Yup.number()
    .required("Order required!")
    .typeError("Order must be a number!")
    .min(1, "Order must be at least 1"),
});

function AddMissionForm({ setOpen, category, mission, isEdit = false }) {
  const navigate = useNavigate();
  const [newObject, setNewObject] = useState({});
  const [goSave, setGoSave] = useState(false);
  const [goEdit, setGoEdit] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState("");
  const [taskType, setTaskType] = useState("mcq");
  const [fileError, setFileError] = useState("");
  const [dateTime, setDateTime] = useState(null);
  const [startDateTime, setStartDateTime] = useState(null);
  const [endDateTime, setEndDateTime] = useState(null);
  const [order, setOrder] = useState(1);

  // MCQ states
  const [questions, setQuestions] = useState([]);
  const [mcqImages, setMcqImages] = useState([]);
  const questionRefs = useRef([]);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const isDragging = useRef(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [swapInfo, setSwapInfo] = useState({ from: null, to: null });
  const [questionErrors, setQuestionErrors] = useState(new Set());
  const [questionErrorMessages, setQuestionErrorMessages] = useState({});

  const { saveState, loading: addLoadingSave } = useSave({
    url: missionsURL,
    goSave,
    setGoSave,
    newObject,
    isImage: true,
  });

  const { state: editState, loading: addLoadingEdit } = useUpdate(
    `${missionsURL}/${mission?.id}`,
    goEdit,
    setGoEdit,
    newObject,
    null,
    true
  );

  const addLoading = isEdit ? addLoadingEdit : addLoadingSave;

  useEffect(() => {
    // Prefill date-related state when editing (use dayjs objects for MUI pickers)
    if (isEdit && mission) {
      setDateTime(mission?.dateTime ? dayjs(mission.dateTime) : null);
      setStartDateTime(
        mission?.submissionStartDateTime
          ? dayjs(mission.submissionStartDateTime)
          : null
      );
      setEndDateTime(
        mission?.submissionEndDateTime
          ? dayjs(mission.submissionEndDateTime)
          : null
      );
      setOrder(mission?.order ?? 1);
      if (mission?.task?.type) {
        setTaskType(mission.task.type);
      }
    }
  }, [isEdit, mission]);

  // Prefill MCQ questions when editing an MCQ mission
  useEffect(() => {
    if (
      isEdit &&
      mission &&
      mission.task?.type === "mcq" &&
      mission.task?.missionTaskMCQ?.questions &&
      questions.length === 0
    ) {
      const apiQuestions = mission.task.missionTaskMCQ.questions || [];
      const symbols = ["A", "B", "C", "D"];

      const mappedQuestions = apiQuestions.map((q, qIndex) => {
        const apiAnswers = q.answers || [];

        const answers =
          apiAnswers.length > 0
            ? apiAnswers.map((a, aIndex) => ({
                text: a.text || "",
                imageIndex: null, // new uploads will set this
                imagePath: a.imagePath || null, // existing image path for display
                imageName: a.imageName || null, // existing image identifier
                symbol: a.symbol || symbols[aIndex] || "",
                isCorrect: !!a.isCorrect,
              }))
            : symbols.map((symbol, index) => ({
                text: "",
                imageIndex: null,
                imagePath: null,
                imageName: null,
                symbol,
                isCorrect: index === 0,
              }));

        return {
          text: q.text || "",
          imageIndex: null, // new uploads will set this
          imagePath: q.imagePath || null, // existing image path for display
          imageName: q.imageName || null, // existing image identifier
          score: q.score ?? 0,
          answers,
        };
      });

      setQuestions(mappedQuestions);
    }
  }, [isEdit, mission, questions.length]);

  useEffect(() => {
    if (saveState?.dataRes?.status == "success") {
      navigate(`/categories/${category.id}/ageGroups/${category.id}`);
    }
  }, [isEdit, saveState, category, navigate]);

  useEffect(() => {
    if (editState?.dataRes?.status == "success") {
      navigate(`/missions/${mission?.id}`);
    }
  }, [isEdit, editState, mission, navigate]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (10MB = 10 * 1024 * 1024 bytes)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes

      if (file.size > maxSize) {
        setFileError("File size must be less than 10MB");
        setSelectedFile(null);
        setFileName("");
        // Clear the input
        event.target.value = "";
      } else {
        setSelectedFile(file);
        setFileName(file.name);
        setFileError("");
      }
    }
  };

  const handleFileUpload = () => {
    const fileInput = document.getElementById("file-upload");
    fileInput.click();
  };

  // MCQ Helper Functions
  const addQuestion = () => {
    const symbols = ["A", "B", "C", "D"];
    const newQuestion = {
      text: "",
      imageIndex: null,
      imagePath: null,
      imageName: null,
      score: 10,
      answers: symbols.map((symbol, index) => ({
        text: "",
        imageIndex: null,
        imagePath: null,
        imageName: null,
        symbol: symbol,
        isCorrect: index === 0, // First answer is correct by default
      })),
    };
    const newIndex = questions.length;
    setQuestions([...questions, newQuestion]);

    // Scroll to the new question after it's rendered
    setTimeout(() => {
      scrollToQuestion(newIndex);
    }, 100);
  };

  const scrollToQuestion = (questionIndex) => {
    // Don't scroll if we just finished dragging
    if (isDragging.current) {
      isDragging.current = false;
      return;
    }
    if (questionRefs.current[questionIndex]) {
      questionRefs.current[questionIndex].scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, index) => {
    isDragging.current = true;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", index.toString());
    // Set opacity on the dragged element
    if (e.target) {
      e.target.style.opacity = "0.5";
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";
    if (draggedIndex !== null && draggedIndex !== index) {
      setDragOverIndex(index);
    }
  };

  const handleDragLeave = (e) => {
    // Only clear if we're actually leaving the element (not just moving to a child)
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      setDragOverIndex(null);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverIndex(null);

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      isDragging.current = false;
      return;
    }

    // Calculate the target position for animation
    // We want the item to visually move to dropIndex position
    let targetIndex = dropIndex;

    // If dragging down, we want to insert AFTER the dropIndex item
    // If dragging up, we want to insert AT the dropIndex position
    if (draggedIndex < dropIndex) {
      // Dragging down: target is dropIndex (we'll insert after removing, which will be at dropIndex)
      targetIndex = dropIndex;
    } else {
      // Dragging up: target is dropIndex
      targetIndex = dropIndex;
    }

    // Set swap info for animation (using current indices before reordering)
    setSwapInfo({ from: draggedIndex, to: targetIndex });
    setIsAnimating(true);

    // Reorder questions
    const newQuestions = [...questions];
    const draggedQuestion = newQuestions[draggedIndex];

    // Remove the dragged item first
    newQuestions.splice(draggedIndex, 1);

    // Calculate the correct insertion index after removal
    let insertIndex;
    if (draggedIndex < dropIndex) {
      // Dragging down: after removing from earlier position,
      // to insert at dropIndex position, we use dropIndex (not dropIndex - 1)
      // because we want it to be at the dropIndex position, not before it
      insertIndex = dropIndex;
    } else {
      // Dragging up: dropIndex remains the same
      insertIndex = dropIndex;
    }

    // Insert at calculated position
    newQuestions.splice(insertIndex, 0, draggedQuestion);

    // Update questions array after a small delay to allow animation to start
    setTimeout(() => {
      setQuestions(newQuestions);
      setDraggedIndex(null);

      // Update error indices after reordering
      if (
        questionErrors.size > 0 ||
        Object.keys(questionErrorMessages).length > 0
      ) {
        const newErrors = new Set();
        const newErrorMessages = {};
        questionErrors.forEach((errorIndex) => {
          if (errorIndex === draggedIndex) {
            // The dragged item's error moves to the new position
            newErrors.add(targetIndex);
            if (questionErrorMessages[draggedIndex]) {
              newErrorMessages[targetIndex] =
                questionErrorMessages[draggedIndex];
            }
          } else if (draggedIndex < targetIndex) {
            // Dragging down: items between draggedIndex and targetIndex shift up
            if (errorIndex > draggedIndex && errorIndex <= targetIndex) {
              newErrors.add(errorIndex - 1);
              if (questionErrorMessages[errorIndex]) {
                newErrorMessages[errorIndex - 1] =
                  questionErrorMessages[errorIndex];
              }
            } else {
              newErrors.add(errorIndex);
              if (questionErrorMessages[errorIndex]) {
                newErrorMessages[errorIndex] =
                  questionErrorMessages[errorIndex];
              }
            }
          } else {
            // Dragging up: items between targetIndex and draggedIndex shift down
            if (errorIndex >= targetIndex && errorIndex < draggedIndex) {
              newErrors.add(errorIndex + 1);
              if (questionErrorMessages[errorIndex]) {
                newErrorMessages[errorIndex + 1] =
                  questionErrorMessages[errorIndex];
              }
            } else {
              newErrors.add(errorIndex);
              if (questionErrorMessages[errorIndex]) {
                newErrorMessages[errorIndex] =
                  questionErrorMessages[errorIndex];
              }
            }
          }
        });
        setQuestionErrors(newErrors);
        setQuestionErrorMessages(newErrorMessages);
      }

      // End animation after transition completes
      setTimeout(() => {
        setIsAnimating(false);
        setSwapInfo({ from: null, to: null });
        isDragging.current = false;
      }, 300);
    }, 10);
  };

  const handleDragEnd = (e) => {
    // Reset opacity on all elements
    const draggedElement = e.target;
    if (draggedElement) {
      draggedElement.style.opacity = "1";
    }
    // Also reset any child elements
    const allElements = document.querySelectorAll('[draggable="true"]');
    allElements.forEach((el) => {
      el.style.opacity = "1";
    });
    setDraggedIndex(null);
    setDragOverIndex(null);
    // Reset dragging flag after a short delay
    setTimeout(() => {
      isDragging.current = false;
    }, 100);
  };

  const removeQuestion = (questionIndex) => {
    // No need to remove images from array since they're stored as File objects directly
    // Just remove the question from the array
    setQuestions(questions.filter((_, index) => index !== questionIndex));
    // Remove error for this question and adjust indices
    if (questionErrors.size > 0) {
      const newErrors = new Set();
      const newErrorMessages = {};
      questionErrors.forEach((errorIndex) => {
        if (errorIndex < questionIndex) {
          newErrors.add(errorIndex);
          if (questionErrorMessages[errorIndex]) {
            newErrorMessages[errorIndex] = questionErrorMessages[errorIndex];
          }
        } else if (errorIndex > questionIndex) {
          newErrors.add(errorIndex - 1);
          if (questionErrorMessages[errorIndex]) {
            newErrorMessages[errorIndex - 1] =
              questionErrorMessages[errorIndex];
          }
        }
      });
      setQuestionErrors(newErrors);
      setQuestionErrorMessages(newErrorMessages);
    }
  };

  const updateQuestion = (questionIndex, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex] = {
      ...updatedQuestions[questionIndex],
      [field]: value,
    };
    setQuestions(updatedQuestions);
    // Re-validate this question to check if all errors are resolved
    if (questionErrors.has(questionIndex)) {
      const newQuestionErrors = validateSingleQuestion(
        updatedQuestions[questionIndex]
      );
      if (newQuestionErrors.length === 0) {
        // All errors resolved, remove from error sets
        const newErrors = new Set(questionErrors);
        newErrors.delete(questionIndex);
        setQuestionErrors(newErrors);
        const newErrorMessages = { ...questionErrorMessages };
        delete newErrorMessages[questionIndex];
        setQuestionErrorMessages(newErrorMessages);
      } else {
        // Update error messages
        setQuestionErrorMessages({
          ...questionErrorMessages,
          [questionIndex]: newQuestionErrors,
        });
      }
    }
  };

  // const addAnswer = (questionIndex) => {
  //   const updatedQuestions = [...questions];
  //   const currentAnswers = updatedQuestions[questionIndex].answers;
  //   const symbols = ["a", "b", "c", "d", "e", "f", "g", "h"];
  //   const usedSymbols = currentAnswers.map((a) => a.symbol);
  //   const nextSymbol = symbols.find((s) => !usedSymbols.includes(s)) || "a";

  //   updatedQuestions[questionIndex].answers.push({
  //     text: "",
  //     imageIndex: null,
  //     symbol: nextSymbol,
  //     isCorrect: false,
  //   });
  //   setQuestions(updatedQuestions);
  //   // Re-validate this question to check if all errors are resolved
  //   if (questionErrors.has(questionIndex)) {
  //     const newQuestionErrors = validateSingleQuestion(
  //       updatedQuestions[questionIndex]
  //     );
  //     if (newQuestionErrors.length === 0) {
  //       // All errors resolved, remove from error sets
  //       const newErrors = new Set(questionErrors);
  //       newErrors.delete(questionIndex);
  //       setQuestionErrors(newErrors);
  //       const newErrorMessages = { ...questionErrorMessages };
  //       delete newErrorMessages[questionIndex];
  //       setQuestionErrorMessages(newErrorMessages);
  //     } else {
  //       // Update error messages
  //       setQuestionErrorMessages({
  //         ...questionErrorMessages,
  //         [questionIndex]: newQuestionErrors,
  //       });
  //     }
  //   }
  // };

  // const removeAnswer = (questionIndex, answerIndex) => {
  //   const updatedQuestions = [...questions];
  //   const answer = updatedQuestions[questionIndex].answers[answerIndex];
  //   // Remove answer image if exists
  //   if (answer.imageIndex !== null) {
  //     removeImage(answer.imageIndex);
  //   }
  //   updatedQuestions[questionIndex].answers = updatedQuestions[
  //     questionIndex
  //   ].answers.filter((_, index) => index !== answerIndex);
  //   setQuestions(updatedQuestions);
  //   // Re-validate this question to check if all errors are resolved
  //   if (questionErrors.has(questionIndex)) {
  //     const newQuestionErrors = validateSingleQuestion(
  //       updatedQuestions[questionIndex]
  //     );
  //     if (newQuestionErrors.length === 0) {
  //       // All errors resolved, remove from error sets
  //       const newErrors = new Set(questionErrors);
  //       newErrors.delete(questionIndex);
  //       setQuestionErrors(newErrors);
  //       const newErrorMessages = { ...questionErrorMessages };
  //       delete newErrorMessages[questionIndex];
  //       setQuestionErrorMessages(newErrorMessages);
  //     } else {
  //       // Update error messages
  //       setQuestionErrorMessages({
  //         ...questionErrorMessages,
  //         [questionIndex]: newQuestionErrors,
  //       });
  //     }
  //   }
  // };

  const updateAnswer = (questionIndex, answerIndex, field, value) => {
    const updatedQuestions = [...questions];
    updatedQuestions[questionIndex].answers[answerIndex] = {
      ...updatedQuestions[questionIndex].answers[answerIndex],
      [field]: value,
    };
    // If setting isCorrect to true, set all other answers to false
    if (field === "isCorrect" && value === true) {
      updatedQuestions[questionIndex].answers.forEach((answer, index) => {
        if (index !== answerIndex) {
          answer.isCorrect = false;
        }
      });
    }
    setQuestions(updatedQuestions);
    // Re-validate this question to check if all errors are resolved
    if (questionErrors.has(questionIndex)) {
      const newQuestionErrors = validateSingleQuestion(
        updatedQuestions[questionIndex]
      );
      if (newQuestionErrors.length === 0) {
        // All errors resolved, remove from error sets
        const newErrors = new Set(questionErrors);
        newErrors.delete(questionIndex);
        setQuestionErrors(newErrors);
        const newErrorMessages = { ...questionErrorMessages };
        delete newErrorMessages[questionIndex];
        setQuestionErrorMessages(newErrorMessages);
      } else {
        // Update error messages
        setQuestionErrorMessages({
          ...questionErrorMessages,
          [questionIndex]: newQuestionErrors,
        });
      }
    }
  };

  const handleQuestionImageUpload = (questionIndex, event) => {
    const file = event.target.files[0];
    if (file) {
      // Allowed file types
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
      ];
      const allowedExtensions = [".png", ".jpg", ".jpeg", ".webp"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      // Check file type
      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        setFileError("Only PNG, JPG, JPEG, and WEBP images are allowed");
        event.target.value = "";
        return;
      }

      // Check file size
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setFileError("File size must be less than 10MB");
        event.target.value = "";
        return;
      }

      // Store File object directly in imageIndex (will be converted to array index on submit)
      const updatedQuestions = [...questions];
      updatedQuestions[questionIndex] = {
        ...updatedQuestions[questionIndex],
        imageIndex: file, // Store File object directly
        imageName: null,
        imagePath: null,
      };
      setQuestions(updatedQuestions);
      setFileError("");
    }
    event.target.value = "";
  };

  const handleAnswerImageUpload = (questionIndex, answerIndex, event) => {
    const file = event.target.files[0];
    if (file) {
      // Allowed file types
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "image/jpg",
        "image/webp",
      ];
      const allowedExtensions = [".png", ".jpg", ".jpeg", ".webp"];
      const fileExtension = file.name
        .toLowerCase()
        .substring(file.name.lastIndexOf("."));

      // Check file type
      if (
        !allowedTypes.includes(file.type) &&
        !allowedExtensions.includes(fileExtension)
      ) {
        setFileError("Only PNG, JPG, JPEG, and WEBP images are allowed");
        event.target.value = "";
        return;
      }

      // Check file size
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        setFileError("File size must be less than 10MB");
        event.target.value = "";
        return;
      }

      // Store File object directly in imageIndex (will be converted to array index on submit)
      const updatedQuestions = [...questions];
      updatedQuestions[questionIndex].answers[answerIndex] = {
        ...updatedQuestions[questionIndex].answers[answerIndex],
        imageIndex: file, // Store File object directly
        imageName: null,
        imagePath: null,
      };
      setQuestions(updatedQuestions);
      setFileError("");
      // Re-validate this question to check if all errors are resolved
      if (questionErrors.has(questionIndex)) {
        const newQuestionErrors = validateSingleQuestion(
          updatedQuestions[questionIndex]
        );
        if (newQuestionErrors.length === 0) {
          // All errors resolved, remove from error sets
          const newErrors = new Set(questionErrors);
          newErrors.delete(questionIndex);
          setQuestionErrors(newErrors);
          const newErrorMessages = { ...questionErrorMessages };
          delete newErrorMessages[questionIndex];
          setQuestionErrorMessages(newErrorMessages);
        } else {
          // Update error messages
          setQuestionErrorMessages({
            ...questionErrorMessages,
            [questionIndex]: newQuestionErrors,
          });
        }
      }
    }
    event.target.value = "";
  };

  // Note: removeImage is no longer needed since we store File objects directly
  // Images are removed by setting imageIndex to null in delete handlers

  const getImagePreview = (imageIndex, imagePath) => {
    // If imageIndex is a File object (new upload), create object URL
    if (imageIndex !== null && imageIndex instanceof File) {
      return URL.createObjectURL(imageIndex);
    }
    // If imageIndex is a number (after submit processing), use mcqImages array
    if (
      imageIndex !== null &&
      typeof imageIndex === "number" &&
      mcqImages[imageIndex]
    ) {
      return URL.createObjectURL(mcqImages[imageIndex]);
    }
    // If there's an existing image path, use that
    if (imagePath) {
      return `${BaseURL}/${imagePath}`;
    }
    return null;
  };

  // Validation function for a single question
  const validateSingleQuestion = (questionToValidate) => {
    if (!questionToValidate) return [];

    const questionErrors = [];

    // Validate: question must have text OR image OR both
    const hasQuestionText =
      questionToValidate.text && questionToValidate.text.trim() !== "";
    const hasQuestionImage =
      questionToValidate.imageIndex !== null ||
      questionToValidate.imageName !== null;
    if (!hasQuestionText && !hasQuestionImage) {
      questionErrors.push("Question must have text or image");
    }

    // Validate: score must be greater than 0
    if (questionToValidate.score <= 0) {
      questionErrors.push("Score must be greater than 0");
    }

    // Validate: question must have exactly 4 answers
    if (questionToValidate.answers.length !== 4) {
      questionErrors.push("Question must have exactly 4 answers");
    }

    // Validate: each answer must have text OR image OR both
    questionToValidate.answers.forEach((answer, answerIndex) => {
      const hasAnswerText = answer.text && answer.text.trim() !== "";
      const hasAnswerImage =
        answer.imageIndex !== null || answer.imageName !== null;
      if (!hasAnswerText && !hasAnswerImage) {
        questionErrors.push(
          `Answer ${answer.symbol || answerIndex + 1} must have text or image`
        );
      }
    });

    // Validate: question must have at least one correct answer
    const hasCorrectAnswer = questionToValidate.answers.some(
      (answer) => answer.isCorrect
    );
    if (!hasCorrectAnswer) {
      questionErrors.push("Question must have at least one correct answer");
    }

    return questionErrors;
  };

  // Validation function
  const validateQuestions = () => {
    const errors = new Set();
    const errorMessagesMap = {};

    if (questions.length === 0) {
      toast.error("Please add at least one question!", {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnFocusLoss: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
        theme: "colored",
      });
      return false;
    }

    for (let i = 0; i < questions.length; i++) {
      const questionErrors = validateSingleQuestion(questions[i]);
      if (questionErrors.length > 0) {
        errors.add(i);
        errorMessagesMap[i] = questionErrors;
      }
    }

    if (errors.size > 0) {
      setQuestionErrors(errors);
      setQuestionErrorMessages(errorMessagesMap);

      // Show only main toast
      const firstErrorIndex = Math.min(...Array.from(errors));
      toast.error(
        `Please fix errors in ${errors.size} question(s). Scrolling to first error...`,
        {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnFocusLoss: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          theme: "colored",
        }
      );

      // Scroll to first error
      setTimeout(() => {
        scrollToQuestion(firstErrorIndex);
      }, 100);

      return false;
    }

    setQuestionErrors(new Set());
    setQuestionErrorMessages({});
    return true;
  };

  return (
    <div className="CourseSmallFormWrapper">
      <Formik
        validationSchema={sessionSchema}
        enableReinitialize={isEdit}
        initialValues={{
          name: isEdit && mission ? mission.name || "" : "",
          dateTime:
            isEdit && mission && mission.dateTime
              ? dayjs(mission.dateTime)
              : null,
          submissionStartDateTime:
            isEdit && mission && mission.submissionStartDateTime
              ? dayjs(mission.submissionStartDateTime)
              : null,
          submissionEndDateTime:
            isEdit && mission && mission.submissionEndDateTime
              ? dayjs(mission.submissionEndDateTime)
              : null,
          order: isEdit && mission && mission.order ? mission.order : 1,
        }}
        onSubmit={(values) => {
          if (taskType === "mcq") {
            // Validate MCQ questions
            if (!validateQuestions()) {
              return;
            }

            // Step 1: Collect all File objects and build mcqImages array
            const imagesArray = [];
            const processedQuestions = questions.map((q) => {
              const processedQuestion = { ...q };

              // Process question image
              if (processedQuestion.imageIndex instanceof File) {
                const imageIndex = imagesArray.length;
                imagesArray.push(processedQuestion.imageIndex);
                processedQuestion.imageIndex = imageIndex; // Replace File with index
              }

              // Process answer images
              processedQuestion.answers = processedQuestion.answers.map((a) => {
                const processedAnswer = { ...a };
                if (processedAnswer.imageIndex instanceof File) {
                  const imageIndex = imagesArray.length;
                  imagesArray.push(processedAnswer.imageIndex);
                  processedAnswer.imageIndex = imageIndex; // Replace File with index
                }
                return processedAnswer;
              });

              return processedQuestion;
            });

            // Step 2: Create FormData for MCQ with images
            const formData = new FormData();
            formData.append("name", values.name);
            if (values.dateTime) {
              formData.append("dateTime", values.dateTime.toISOString());
            }
            if (values.submissionStartDateTime) {
              formData.append(
                "submissionStartDateTime",
                values.submissionStartDateTime.toISOString()
              );
            }
            if (values.submissionEndDateTime) {
              formData.append(
                "submissionEndDateTime",
                values.submissionEndDateTime.toISOString()
              );
            }
            formData.append("order", order);
            formData.append("ageGroupId", category?.id);
            formData.append("taskType", "mcq");

            // Step 3: Append questions with processed imageIndex values
            processedQuestions.forEach((q, qIndex) => {
              const baseQ = `mcqTask[questions][${qIndex}]`;

              if (q.text !== null && q.text !== "") {
                formData.append(`${baseQ}[text]`, q.text || "");
              }

              // If imageIndex is a number, user uploaded a new image
              // If imageName exists, user kept the existing image
              if (
                q.imageIndex !== null &&
                q.imageIndex !== undefined &&
                typeof q.imageIndex === "number"
              ) {
                formData.append(`${baseQ}[imageIndex]`, q.imageIndex);
              } else if (q.imageName !== null && q.imageName !== undefined) {
                formData.append(`${baseQ}[imageName]`, q.imageName);
              }

              formData.append(`${baseQ}[score]`, q.score);

              q.answers.forEach((a, aIndex) => {
                const baseA = `${baseQ}[answers][${aIndex}]`;

                if (a.text !== null && a.text !== "") {
                  formData.append(`${baseA}[text]`, a.text || "");
                }

                // If imageIndex is a number, user uploaded a new image
                // If imageName exists, user kept the existing image
                if (
                  a.imageIndex !== null &&
                  a.imageIndex !== undefined &&
                  typeof a.imageIndex === "number"
                ) {
                  formData.append(`${baseA}[imageIndex]`, a.imageIndex);
                } else if (a.imageName !== null && a.imageName !== undefined) {
                  formData.append(`${baseA}[imageName]`, a.imageName);
                }

                formData.append(`${baseA}[symbol]`, a.symbol);
                formData.append(
                  `${baseA}[isCorrect]`,
                  a.isCorrect ? "true" : "false"
                );
              });
            });

            // Step 4: Append all images to FormData
            imagesArray.forEach((image) => {
              formData.append("mcqImages[]", image);
            });

            setNewObject(formData);
          } else {
            // Mission or Discussion type
            setNewObject(
              selectedFile
                ? {
                    ...values,
                    order: order,
                    ageGroupId: category?.id,
                    taskFile: selectedFile,
                    taskType: taskType,
                  }
                : {
                    ...values,
                    order: order,
                    ageGroupId: category?.id,
                    taskType: taskType,
                  }
            );
          }
          if (isEdit) {
            setGoEdit(true);
          } else {
            setGoSave(true);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className="ClassSessionFormContainer">
              <h2 style={{ marginBottom: "20px" }}>
                {isEdit ? "Edit Mission" : "Add New Mission"}
              </h2>
              <TextFieldInput
                fullWidth
                type="text"
                placeholder={"Enter Mission Name"}
                className="Input"
                label={"Mission Name"}
                name={"name"}
                change={handleChange}
                blur={handleBlur}
                newValue={values.name}
              />
              {errors.name && touched.name && errors.name && (
                <p className="error">
                  {errors.name && touched.name && errors.name}
                </p>
              )}
              {/* DateTime Pickers for Start and End */}
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DemoContainer components={["DatePicker"]}>
                  <Box sx={{ width: "100%" }}>
                    <DateTimePicker
                      label="Date / Time"
                      value={dateTime}
                      onChange={(newValue) => {
                        setDateTime(newValue);
                        setFieldValue("dateTime", newValue);
                      }}
                    />
                    {errors.dateTime && touched.dateTime && (
                      <p className="error" style={{ marginTop: "5px" }}>
                        {errors.dateTime}
                      </p>
                    )}
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <DateTimePicker
                      label="Start Date / Time"
                      value={startDateTime}
                      onChange={(newValue) => {
                        setStartDateTime(newValue);
                        setFieldValue("submissionStartDateTime", newValue);
                      }}
                    />
                    {errors.submissionStartDateTime &&
                      touched.submissionStartDateTime && (
                        <p className="error" style={{ marginTop: "5px" }}>
                          {errors.submissionStartDateTime}
                        </p>
                      )}
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <DateTimePicker
                      label="End Date / Time"
                      value={endDateTime}
                      onChange={(newValue) => {
                        setEndDateTime(newValue);
                        setFieldValue("submissionEndDateTime", newValue);
                      }}
                      style={{ marginTop: "10px" }}
                    />
                    {errors.submissionEndDateTime &&
                      touched.submissionEndDateTime && (
                        <p className="error" style={{ marginTop: "5px" }}>
                          {errors.submissionEndDateTime}
                        </p>
                      )}
                  </Box>
                </DemoContainer>
              </LocalizationProvider>

              <div style={{ marginTop: "10px", width: "100%" }}>
                <TextFieldInput
                  fullWidth
                  type="number"
                  placeholder={"Enter Mission Order"}
                  className="Input"
                  label={"Order"}
                  name={"order"}
                  change={(e) => setOrder(parseInt(e.target.value) || 1)}
                  blur={handleBlur}
                  newValue={order}
                />
                {errors.order && touched.order && (
                  <p className="error">{errors.order}</p>
                )}
              </div>

              {/* Task Type Selection */}
              <div style={{ marginTop: "10px", width: "100%" }}>
                <FormControl fullWidth>
                  <InputLabel id="task-type-label">Mission Type</InputLabel>
                  <Select
                    labelId="task-type-label"
                    value={taskType}
                    label="Mission Type"
                    onChange={(e) => {
                      setTaskType(e.target.value);
                      // Clear questions when switching away from MCQ
                      if (e.target.value !== "mcq") {
                        setQuestions([]);
                        setMcqImages([]);
                      }
                    }}
                    style={{ marginBottom: "10px" }}
                  >
                    <MenuItem value="mcq">MCQ Exam</MenuItem>
                    <MenuItem value="mission">Mission</MenuItem>
                    <MenuItem value="discussion">Discussion</MenuItem>
                  </Select>
                </FormControl>
              </div>

              {/* File Upload - Only show for mission and discussion types */}
              {taskType !== "mcq" && (
                <div style={{ marginTop: "5px" }}>
                  <input
                    id="file-upload"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept=".pdf"
                  />
                  <Button
                    variant="outlined"
                    component="span"
                    onClick={handleFileUpload}
                    startIcon={<CloudUploadIcon />}
                    style={{
                      width: "100%",
                      padding: "12px",
                      borderColor: "var(--primary)",
                      color: "var(--primary)",
                      marginBottom: "10px",
                    }}
                  >
                    {fileName ? `Selected: ${fileName}` : "Upload Task File"}
                  </Button>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "var(--text-primary)",
                      margin: "0 0 10px 0",
                      fontStyle: "italic",
                    }}
                  >
                    Maximum file size: 10MB
                  </p>
                  {fileName && (
                    <p
                      style={{
                        fontSize: "12px",
                        color: "var(--text-primary)",
                        margin: "5px 0",
                        fontStyle: "italic",
                      }}
                    >
                      File selected: {fileName}
                    </p>
                  )}
                  {fileError && (
                    <p className="error" style={{ marginTop: "5px" }}>
                      {fileError}
                    </p>
                  )}
                </div>
              )}

              {/* MCQ Question Builder */}
              {taskType === "mcq" && (
                <>
                  <Box
                    sx={{
                      marginTop: "10px",
                      width: "100%",
                      display: "flex",
                      gap: "20px",
                      alignItems: "flex-start",
                    }}
                  >
                    {/* Sidebar Navigation */}
                    <Box
                      className="mcqSidebar"
                      sx={{
                        width: "250px",
                        minWidth: "250px",
                        position: "sticky",
                        top: "20px",
                        backgroundColor: "#f5f5f5",
                        borderRadius: "8px",
                        padding: "15px",
                        maxHeight: "calc(100vh - 100px)",
                        overflowY: "auto",
                        boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                        display: { xs: "none", md: "block" },
                      }}
                    >
                      <Typography
                        variant="h6"
                        sx={{
                          marginBottom: "15px",
                          fontWeight: "bold",
                          color: "var(--primary)",
                        }}
                      >
                        Questions Index
                      </Typography>

                      {/* Total Score Display */}
                      {questions.length > 0 && (
                        <Box
                          sx={{
                            marginBottom: "15px",
                            padding: "10px",
                            backgroundColor: "var(--primary)",
                            borderRadius: "6px",
                            textAlign: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              color: "white",
                              fontWeight: 500,
                              marginBottom: "5px",
                            }}
                          >
                            Total Score
                          </Typography>
                          <Typography
                            variant="h5"
                            sx={{
                              color: "white",
                              fontWeight: "bold",
                            }}
                          >
                            {questions.reduce(
                              (total, question) =>
                                total + (parseInt(question.score) || 0),
                              0
                            )}
                          </Typography>
                        </Box>
                      )}

                      {questions.length === 0 ? (
                        <Typography
                          variant="body2"
                          sx={{ color: "#999", textAlign: "center", py: 2 }}
                        >
                          No questions yet
                        </Typography>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "8px",
                          }}
                        >
                          {questions.map((question, index) => {
                            // Calculate swap animation offset
                            let swapOffset = 0;
                            if (
                              isAnimating &&
                              swapInfo.from !== null &&
                              swapInfo.to !== null
                            ) {
                              const from = swapInfo.from;
                              const to = swapInfo.to;

                              if (index === from) {
                                // Dragged item moves to new position
                                swapOffset = (to - from) * 56; // ~56px per item (button height + gap)
                              } else if (from < to) {
                                // Moving down: items between from and to shift up
                                if (index > from && index <= to) {
                                  swapOffset = -56;
                                }
                              } else if (from > to) {
                                // Moving up: items between to and from shift down
                                if (index >= to && index < from) {
                                  swapOffset = 56;
                                }
                              }
                            }

                            return (
                              <Box
                                key={`question-sidebar-${index}`}
                                draggable
                                className={
                                  isAnimating && swapOffset !== 0
                                    ? "question-swap-animating"
                                    : ""
                                }
                                onDragStart={(e) => handleDragStart(e, index)}
                                onDragOver={(e) => handleDragOver(e, index)}
                                onDragLeave={handleDragLeave}
                                onDrop={(e) => handleDrop(e, index)}
                                onDragEnd={handleDragEnd}
                                sx={{
                                  cursor: "move",
                                  opacity: draggedIndex === index ? 0.5 : 1,
                                  transform:
                                    isAnimating && swapOffset !== 0
                                      ? `translateY(${swapOffset}px)`
                                      : dragOverIndex === index &&
                                        draggedIndex !== index
                                      ? "translateY(-2px)"
                                      : "translateY(0)",
                                  transition: isAnimating
                                    ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                    : "all 0.2s ease",
                                  border:
                                    dragOverIndex === index &&
                                    draggedIndex !== index
                                      ? "2px dashed var(--primary)"
                                      : "2px solid transparent",
                                  borderRadius: "4px",
                                  padding: "2px",
                                  zIndex:
                                    isAnimating && swapOffset !== 0 ? 10 : 1,
                                }}
                              >
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  onClick={() => scrollToQuestion(index)}
                                  sx={{
                                    justifyContent: "space-between",
                                    textTransform: "none",
                                    padding: "10px 15px",
                                    borderColor: questionErrors.has(index)
                                      ? "red"
                                      : "var(--primary)",
                                    color: questionErrors.has(index)
                                      ? "red"
                                      : "var(--primary)",
                                    cursor: "move",
                                    "&:hover": {
                                      backgroundColor: questionErrors.has(index)
                                        ? "rgba(255, 0, 0, 0.1)"
                                        : "var(--primary)",
                                      color: questionErrors.has(index)
                                        ? "red"
                                        : "white",
                                      borderColor: questionErrors.has(index)
                                        ? "red"
                                        : "var(--primary)",
                                    },
                                  }}
                                >
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                    }}
                                  >
                                    {questionErrors.has(index) && (
                                      <ErrorIcon
                                        sx={{
                                          color: "red",
                                          fontSize: "18px",
                                        }}
                                      />
                                    )}
                                    <Typography
                                      variant="body2"
                                      sx={{ fontWeight: 500 }}
                                    >
                                      Question {index + 1}
                                    </Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      cursor: "grab",
                                      "&:active": {
                                        cursor: "grabbing",
                                      },
                                    }}
                                  >
                                    <svg
                                      width="16"
                                      height="16"
                                      viewBox="0 0 16 16"
                                      fill="currentColor"
                                    >
                                      <circle cx="4" cy="4" r="1.5" />
                                      <circle cx="12" cy="4" r="1.5" />
                                      <circle cx="4" cy="8" r="1.5" />
                                      <circle cx="12" cy="8" r="1.5" />
                                      <circle cx="4" cy="12" r="1.5" />
                                      <circle cx="12" cy="12" r="1.5" />
                                    </svg>
                                  </Box>
                                </Button>
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      <Button
                        variant="contained"
                        fullWidth
                        startIcon={<AddIcon />}
                        onClick={addQuestion}
                        sx={{
                          marginTop: "15px",
                          backgroundColor: "var(--primary)",
                          color: "var(--text-secondary)",
                          "&:hover": {
                            backgroundColor: "var(--primary)",
                            opacity: 0.9,
                          },
                        }}
                      >
                        Add New Question
                      </Button>
                    </Box>

                    {/* Questions Content */}
                    <Box
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      {/* Mobile Sidebar - Only visible on small screens */}
                      <Box
                        sx={{
                          display: { xs: "block", md: "none" },
                          marginBottom: "20px",
                          backgroundColor: "#f5f5f5",
                          borderRadius: "8px",
                          padding: "15px",
                          boxShadow: "0px 2px 8px rgba(0,0,0,0.1)",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            marginBottom: "15px",
                            fontWeight: "bold",
                            color: "var(--primary)",
                          }}
                        >
                          Questions Index
                        </Typography>

                        {/* Total Score Display */}
                        {questions.length > 0 && (
                          <Box
                            sx={{
                              marginBottom: "15px",
                              padding: "10px",
                              backgroundColor: "var(--primary)",
                              borderRadius: "6px",
                              textAlign: "center",
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                color: "white",
                                fontWeight: 500,
                                marginBottom: "5px",
                              }}
                            >
                              Total Score
                            </Typography>
                            <Typography
                              variant="h5"
                              sx={{
                                color: "white",
                                fontWeight: "bold",
                              }}
                            >
                              {questions.reduce(
                                (total, question) =>
                                  total + (parseInt(question.score) || 0),
                                0
                              )}
                            </Typography>
                          </Box>
                        )}

                        {questions.length === 0 ? (
                          <Typography
                            variant="body2"
                            sx={{ color: "#999", textAlign: "center", py: 2 }}
                          >
                            No questions yet
                          </Typography>
                        ) : (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "8px",
                              maxHeight: "200px",
                              overflowY: "auto",
                            }}
                          >
                            {questions.map((question, index) => {
                              // Calculate swap animation offset for mobile
                              let swapOffset = 0;
                              if (
                                isAnimating &&
                                swapInfo.from !== null &&
                                swapInfo.to !== null
                              ) {
                                const from = swapInfo.from;
                                const to = swapInfo.to;

                                if (index === from) {
                                  // Dragged item moves to new position
                                  swapOffset = (to - from) * 56; // ~56px per item
                                } else if (from < to) {
                                  // Moving down: items between from and to shift up
                                  if (index > from && index <= to) {
                                    swapOffset = -56;
                                  }
                                } else if (from > to) {
                                  // Moving up: items between to and from shift down
                                  if (index >= to && index < from) {
                                    swapOffset = 56;
                                  }
                                }
                              }

                              return (
                                <Box
                                  key={`question-mobile-${index}`}
                                  draggable
                                  className={
                                    isAnimating && swapOffset !== 0
                                      ? "question-swap-animating"
                                      : ""
                                  }
                                  onDragStart={(e) => handleDragStart(e, index)}
                                  onDragOver={(e) => handleDragOver(e, index)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDrop(e, index)}
                                  onDragEnd={handleDragEnd}
                                  sx={{
                                    cursor: "move",
                                    opacity: draggedIndex === index ? 0.5 : 1,
                                    transform:
                                      isAnimating && swapOffset !== 0
                                        ? `translateY(${swapOffset}px)`
                                        : dragOverIndex === index &&
                                          draggedIndex !== index
                                        ? "translateY(-2px)"
                                        : "translateY(0)",
                                    transition: isAnimating
                                      ? "transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                      : "all 0.2s ease",
                                    border:
                                      dragOverIndex === index &&
                                      draggedIndex !== index
                                        ? "2px dashed var(--primary)"
                                        : "2px solid transparent",
                                    borderRadius: "4px",
                                    padding: "2px",
                                    zIndex:
                                      isAnimating && swapOffset !== 0 ? 10 : 1,
                                  }}
                                >
                                  <Button
                                    variant="outlined"
                                    fullWidth
                                    onClick={() => scrollToQuestion(index)}
                                    sx={{
                                      justifyContent: "space-between",
                                      textTransform: "none",
                                      padding: "10px 15px",
                                      borderColor: questionErrors.has(index)
                                        ? "red"
                                        : "var(--primary)",
                                      color: questionErrors.has(index)
                                        ? "red"
                                        : "var(--primary)",
                                      cursor: "move",
                                      "&:hover": {
                                        backgroundColor: questionErrors.has(
                                          index
                                        )
                                          ? "rgba(255, 0, 0, 0.1)"
                                          : "var(--primary)",
                                        color: questionErrors.has(index)
                                          ? "red"
                                          : "white",
                                        borderColor: questionErrors.has(index)
                                          ? "red"
                                          : "var(--primary)",
                                      },
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "8px",
                                      }}
                                    >
                                      {questionErrors.has(index) && (
                                        <ErrorIcon
                                          sx={{
                                            color: "red",
                                            fontSize: "18px",
                                          }}
                                        />
                                      )}
                                      <Typography
                                        variant="body2"
                                        sx={{ fontWeight: 500 }}
                                      >
                                        Question {index + 1}
                                      </Typography>
                                    </Box>
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        cursor: "grab",
                                        "&:active": {
                                          cursor: "grabbing",
                                        },
                                      }}
                                    >
                                      <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 16 16"
                                        fill="currentColor"
                                      >
                                        <circle cx="4" cy="4" r="1.5" />
                                        <circle cx="12" cy="4" r="1.5" />
                                        <circle cx="4" cy="8" r="1.5" />
                                        <circle cx="12" cy="8" r="1.5" />
                                        <circle cx="4" cy="12" r="1.5" />
                                        <circle cx="12" cy="12" r="1.5" />
                                      </svg>
                                    </Box>
                                  </Button>
                                </Box>
                              );
                            })}
                          </Box>
                        )}

                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<AddIcon />}
                          onClick={addQuestion}
                          sx={{
                            marginTop: "15px",
                            backgroundColor: "var(--primary)",
                            color: "var(--text-secondary)",
                            "&:hover": {
                              backgroundColor: "var(--primary)",
                              opacity: 0.9,
                            },
                          }}
                        >
                          Add New Question
                        </Button>
                      </Box>
                      {questions.length === 0 && (
                        <Box
                          sx={{
                            textAlign: "center",
                            padding: "40px 20px 10px",
                            backgroundColor: "#f9f9f9",
                            borderRadius: "8px",
                          }}
                        >
                          <Typography
                            variant="body1"
                            sx={{ color: "#999", marginBottom: "10px" }}
                          >
                            No questions added yet. Click "Add New Question" to
                            get started.
                          </Typography>
                        </Box>
                      )}

                      {questions.map((question, questionIndex) => (
                        <Card
                          key={questionIndex}
                          ref={(el) =>
                            (questionRefs.current[questionIndex] = el)
                          }
                          sx={{
                            marginBottom: "20px",
                            padding: "10px",
                            position: "relative",
                            scrollMarginTop: "20px",
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginBottom: "15px",
                              }}
                            >
                              <h3>Question {questionIndex + 1}</h3>
                              <IconButton
                                sx={{
                                  color: "red",
                                  borderRadius: "10px",
                                  padding: "10px 20px",
                                }}
                                onClick={() => removeQuestion(questionIndex)}
                              >
                                <span
                                  style={{
                                    fontSize: "18px",
                                    marginRight: "5px",
                                  }}
                                >
                                  Delete Question
                                </span>
                                <DeleteIcon />
                              </IconButton>
                            </Box>

                            {/* Error Messages */}
                            {questionErrors.has(questionIndex) &&
                              questionErrorMessages[questionIndex] &&
                              questionErrorMessages[questionIndex].length >
                                0 && (
                                <Box
                                  sx={{
                                    marginBottom: "15px",
                                    padding: "10px",
                                    backgroundColor: "#ffebee",
                                    borderRadius: "4px",
                                    border: "1px solid #ef5350",
                                  }}
                                >
                                  {questionErrorMessages[questionIndex].map(
                                    (errorMsg, errorIndex) => (
                                      <Typography
                                        key={errorIndex}
                                        variant="body2"
                                        sx={{
                                          color: "#d32f2f",
                                          marginBottom:
                                            errorIndex <
                                            questionErrorMessages[questionIndex]
                                              .length -
                                              1
                                              ? "5px"
                                              : "0",
                                        }}
                                      >
                                        • {errorMsg}
                                      </Typography>
                                    )
                                  )}
                                </Box>
                              )}

                            {/* Question Text */}
                            <Typography
                              variant="body2"
                              sx={{ marginBottom: "5px", fontWeight: 500 }}
                            >
                              Question Text (Optional)
                            </Typography>
                            <TextArea
                              placeholder="Enter question text (optional)"
                              label="Question Text"
                              name={`question-${questionIndex}-text`}
                              change={(e) =>
                                updateQuestion(
                                  questionIndex,
                                  "text",
                                  e.target.value
                                )
                              }
                              newValue={question.text}
                              style={{ marginBottom: "15px", width: "100%" }}
                            />

                            {/* Question Image */}
                            <Box sx={{ marginBottom: "5px" }}>
                              <input
                                type="file"
                                accept="image/png,image/jpeg,image/jpg"
                                style={{ display: "none" }}
                                id={`question-image-${questionIndex}`}
                                onChange={(e) =>
                                  handleQuestionImageUpload(questionIndex, e)
                                }
                              />
                              <Button
                                variant="outlined"
                                component="span"
                                startIcon={<ImageIcon />}
                                onClick={() =>
                                  document
                                    .getElementById(
                                      `question-image-${questionIndex}`
                                    )
                                    .click()
                                }
                                style={{
                                  borderColor: "var(--primary)",
                                  color: "var(--primary)",
                                  marginBottom: "10px",
                                }}
                              >
                                {question.imageIndex !== null ||
                                question.imagePath
                                  ? "Change Question Image"
                                  : "Upload Question Image (Optional)"}
                              </Button>
                              {(question.imageIndex !== null ||
                                question.imagePath) && (
                                <Box
                                  sx={{
                                    marginTop: "10px",
                                    position: "relative",
                                    width: "fit-content",
                                  }}
                                >
                                  <img
                                    key={`question-img-${questionIndex}-${
                                      question.imageIndex instanceof File
                                        ? question.imageIndex.name
                                        : question.imageIndex ||
                                          question.imageName ||
                                          "none"
                                    }`}
                                    src={getImagePreview(
                                      question.imageIndex,
                                      question.imagePath
                                    )}
                                    alt="Question"
                                    style={{
                                      maxWidth: "200px",
                                      maxHeight: "200px",
                                      borderRadius: "5px",
                                    }}
                                    onError={(e) => {
                                      console.error(
                                        "Image failed to load:",
                                        e.target.src
                                      );
                                    }}
                                  />

                                  <IconButton
                                    onClick={() => {
                                      // Simply set imageIndex to null (no array management needed)
                                      const updatedQuestions = [...questions];
                                      updatedQuestions[questionIndex] = {
                                        ...updatedQuestions[questionIndex],
                                        imageIndex: null,
                                        imageName: null,
                                        imagePath: null,
                                      };
                                      setQuestions(updatedQuestions);
                                    }}
                                    size="small"
                                    style={{
                                      color: "red",
                                      position: "absolute",
                                      top: "5px",
                                      right: "5px",
                                      background: "white",
                                    }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Box>
                              )}
                            </Box>

                            {/* Question Score */}
                            <TextFieldInput
                              fullWidth
                              type="text"
                              inputMode="numeric"
                              placeholder="Enter score"
                              className="Input"
                              label="Question Score"
                              name={`question-${questionIndex}-score`}
                              change={(e) =>
                                updateQuestion(
                                  questionIndex,
                                  "score",
                                  parseInt(e.target.value) || 0
                                )
                              }
                              newValue={question.score}
                              style={{ marginBottom: "15px" }}
                            />

                            {/* Answers */}
                            <Box sx={{ marginTop: "20px" }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  marginBottom: "15px",
                                }}
                              >
                                <h4>Question {questionIndex + 1} Answers:</h4>
                              </Box>

                              <Grid container spacing={2}>
                                {question.answers.map((answer, answerIndex) => (
                                  <Grid
                                    item
                                    xs={12}
                                    sm={6}
                                    sx={{ height: "100%" }}
                                  >
                                    <Card
                                      key={answerIndex}
                                      sx={{
                                        padding: "15px",
                                        backgroundColor: "#f5f5f5",
                                        position: "relative",
                                        height: "100%",
                                      }}
                                    >
                                      <Box
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                          marginBottom: "10px",
                                        }}
                                      >
                                        <Typography
                                          variant="h6"
                                          sx={{
                                            fontWeight: 600,
                                            color: "var(--primary)",
                                            fontSize: "18px",
                                          }}
                                        >
                                          Answer - {answer.symbol}
                                        </Typography>
                                      </Box>

                                      <Typography
                                        variant="body2"
                                        sx={{
                                          marginBottom: "5px",
                                          fontWeight: 500,
                                        }}
                                      >
                                        Answer Text (Optional)
                                      </Typography>
                                      <TextArea
                                        placeholder="Enter answer text (optional)"
                                        label="Answer Text"
                                        name={`answer-${questionIndex}-${answerIndex}-text`}
                                        change={(e) =>
                                          updateAnswer(
                                            questionIndex,
                                            answerIndex,
                                            "text",
                                            e.target.value
                                          )
                                        }
                                        newValue={answer.text}
                                        style={{
                                          marginBottom: "10px",
                                          width: "100%",
                                        }}
                                      />

                                      {/* Answer Image */}
                                      <Box>
                                        <input
                                          type="file"
                                          accept="image/png,image/jpeg,image/jpg"
                                          style={{ display: "none" }}
                                          id={`answer-image-${questionIndex}-${answerIndex}`}
                                          onChange={(e) =>
                                            handleAnswerImageUpload(
                                              questionIndex,
                                              answerIndex,
                                              e
                                            )
                                          }
                                        />
                                        <Button
                                          variant="outlined"
                                          component="span"
                                          size="small"
                                          startIcon={<ImageIcon />}
                                          onClick={() =>
                                            document
                                              .getElementById(
                                                `answer-image-${questionIndex}-${answerIndex}`
                                              )
                                              .click()
                                          }
                                          style={{
                                            borderColor: "var(--primary)",
                                            color: "var(--primary)",
                                            marginBottom: "10px",
                                          }}
                                        >
                                          {answer.imageIndex !== null ||
                                          answer.imagePath
                                            ? "Change Answer Image"
                                            : "Upload Answer Image (Optional)"}
                                        </Button>
                                        {(answer.imageIndex !== null ||
                                          answer.imagePath) && (
                                          <Box
                                            sx={{
                                              position: "relative",
                                              width: "fit-content",
                                            }}
                                          >
                                            <img
                                              key={`answer-img-${questionIndex}-${answerIndex}-${
                                                answer.imageIndex instanceof
                                                File
                                                  ? answer.imageIndex.name
                                                  : answer.imageIndex ||
                                                    answer.imageName ||
                                                    "none"
                                              }`}
                                              src={getImagePreview(
                                                answer.imageIndex,
                                                answer.imagePath
                                              )}
                                              alt="Answer"
                                              style={{
                                                maxWidth: "200px",
                                                maxHeight: "200px",
                                                borderRadius: "5px",
                                                width: "100%",
                                                height: "100%",
                                              }}
                                              onError={(e) => {
                                                console.error(
                                                  "Image failed to load:",
                                                  e.target.src
                                                );
                                              }}
                                            />
                                            <IconButton
                                              onClick={() => {
                                                // Simply set imageIndex to null (no array management needed)
                                                const updatedQuestions = [
                                                  ...questions,
                                                ];
                                                updatedQuestions[
                                                  questionIndex
                                                ].answers[answerIndex] = {
                                                  ...updatedQuestions[
                                                    questionIndex
                                                  ].answers[answerIndex],
                                                  imageIndex: null,
                                                  imageName: null,
                                                  imagePath: null,
                                                };
                                                setQuestions(updatedQuestions);
                                              }}
                                              size="small"
                                              style={{
                                                color: "red",
                                                position: "absolute",
                                                top: "5px",
                                                right: "5px",
                                                background: "white",
                                              }}
                                            >
                                              <DeleteIcon />
                                            </IconButton>
                                          </Box>
                                        )}

                                        <Box
                                          sx={{
                                            display: "flex",
                                            alignItems: "center",
                                          }}
                                        >
                                          <Radio
                                            id={`correct-answer-${questionIndex}-${answerIndex}`}
                                            checked={answer.isCorrect}
                                            onChange={(e) =>
                                              updateAnswer(
                                                questionIndex,
                                                answerIndex,
                                                "isCorrect",
                                                e.target.checked
                                              )
                                            }
                                          />
                                          <label
                                            style={{
                                              fontWeight: "bold",
                                              cursor: "pointer",
                                            }}
                                            htmlFor={`correct-answer-${questionIndex}-${answerIndex}`}
                                          >
                                            Correct Answer
                                          </label>
                                        </Box>
                                      </Box>
                                    </Card>
                                  </Grid>
                                ))}
                              </Grid>
                            </Box>
                          </CardContent>
                        </Card>
                      ))}

                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Button
                          variant="contained"
                          fullWidth
                          startIcon={<AddIcon />}
                          onClick={addQuestion}
                          sx={{
                            backgroundColor: "var(--primary)",
                            color: "var(--text-secondary)",
                            "&:hover": {
                              backgroundColor: "var(--primary)",
                              opacity: 0.9,
                            },
                            maxWidth: "220px",
                          }}
                        >
                          Add New Question
                        </Button>
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "center",
                marginTop: "20px",
              }}
            >
              <PrimaryButton
                classBtn={"center addButton"}
                btnType={"submit"}
                text={isEdit ? "Save Changes" : "Submit Mission"}
                loading={addLoading}
                style={{
                  textTransform: "capitalize",
                  marginTop: "15px",
                  width: "200px",
                  borderRadius: "5px",
                  color: "var(--text-secondary)",
                  backgroundColor: "var(--primary)",
                }}
              />
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

export default AddMissionForm;
